import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { loadGoogleMaps } from "utils/loadGoogleMaps";
import { Location } from "types/location";
import {
  PickupTrackingLocation,
  PickupTrackingPosition,
} from "types/pickupTracking";

type LatLng = { lat: number; lng: number };

function hasTrackingLocationCoords(
  location?: PickupTrackingLocation | null,
): location is PickupTrackingLocation & { latitude: number; longitude: number } {
  return (
    location != null &&
    location.latitude != null &&
    location.longitude != null &&
    !Number.isNaN(Number(location.latitude)) &&
    !Number.isNaN(Number(location.longitude))
  );
}

function locationToAddress(location?: Location | null): string | null {
  if (!location) return null;
  const parts = [
    location.address,
    location.address2,
    location.city,
    location.state,
    location.zipcode,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

export default function PickupTrackingMap({
  positions,
  devicePosition,
  startLocation,
  endLocation,
  pickupLocation,
}: {
  positions: PickupTrackingPosition[];
  devicePosition?: LatLng | null;
  startLocation?: PickupTrackingLocation | null;
  endLocation?: PickupTrackingLocation | null;
  pickupLocation?: Location | null;
}) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapRef.current || !window.google?.maps) return;

        const map = new window.google.maps.Map(mapRef.current, {
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        mapInstanceRef.current = map;

        const bounds = new window.google.maps.LatLngBounds();
        const geocoder = new window.google.maps.Geocoder();

        const addMarker = (
          position: LatLng,
          title: string,
          color?: string,
        ) => {
          bounds.extend(position);
          new window.google.maps.Marker({
            map,
            position,
            title,
            icon: color
              ? {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: color,
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 2,
                }
              : undefined,
          });
        };

        if (positions.length > 1) {
          const path = positions.map((p) => ({
            lat: Number(p.latitude),
            lng: Number(p.longitude),
          }));
          path.forEach((point) => bounds.extend(point));
          new window.google.maps.Polyline({
            map,
            path,
            strokeColor: "#225979",
            strokeOpacity: 0.9,
            strokeWeight: 4,
          });
        }

        if (devicePosition) {
          addMarker(devicePosition, "Current location", "#225979");
        }

        if (hasTrackingLocationCoords(startLocation)) {
          addMarker(
            {
              lat: Number(startLocation.latitude),
              lng: Number(startLocation.longitude),
            },
            startLocation.name || "Start",
            "#2e7d32",
          );
        }

        if (hasTrackingLocationCoords(endLocation)) {
          addMarker(
            {
              lat: Number(endLocation.latitude),
              lng: Number(endLocation.longitude),
            },
            endLocation.name || "Destination",
            "#ed6c02",
          );
        }

        const pickupAddress = locationToAddress(pickupLocation);
        const geocodePromises: Promise<void>[] = [];

        if (pickupAddress) {
          geocodePromises.push(
            new Promise((resolve) => {
              geocoder.geocode({ address: pickupAddress }, (results, status) => {
                if (status === "OK" && results?.[0]?.geometry?.location) {
                  const loc = results[0].geometry.location;
                  addMarker(
                    { lat: loc.lat(), lng: loc.lng() },
                    "Pickup site",
                    "#9c27b0",
                  );
                }
                resolve();
              });
            }),
          );
        }

        Promise.all(geocodePromises).then(() => {
          if (bounds.isEmpty() && devicePosition) {
            map.setCenter(devicePosition);
            map.setZoom(12);
          } else if (!bounds.isEmpty()) {
            map.fitBounds(bounds, 48);
          } else {
            map.setCenter({ lat: 39.8283, lng: -98.5795 });
            map.setZoom(4);
          }
        });
      })
      .catch(() => {
        /* map container shows empty; parent handles error state */
      });

    return () => {
      cancelled = true;
      mapInstanceRef.current = null;
    };
  }, [positions, devicePosition, startLocation, endLocation, pickupLocation]);

  return (
    <Box
      ref={mapRef}
      sx={{
        width: "100%",
        height: { xs: 280, sm: 360 },
        borderRadius: 1,
        bgcolor: "grey.100",
      }}
    />
  );
}
